import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Chip,
  InputAdornment,
  Popover,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import {
  DurationFilterUnit,
  DurationFilterMode,
  DurationFilterRangeValue,
  DurationFilterValue,
  formatDurationFilterValue,
  isDurationFilterRangeValue,
  normalizeDurationFilterAmount,
} from "@/components/tables/genericCrudTableDurationFilterUtils";
import styles from "@/components/tables/genericCrudTableDurationFilter.module.css";

const QUICK_VALUES: Record<DurationFilterUnit, string[]> = {
  minutes: ["15", "30", "45", "60"],
  hours: ["1", "2", "4", "8"],
};

const QUICK_RANGES: Record<
  DurationFilterUnit,
  Array<{ from: string; to: string; label: string }>
> = {
  minutes: [
    { from: "0", to: "30", label: "0–30m" },
    { from: "30", to: "60", label: "30–60m" },
    { from: "60", to: "120", label: "60–120m" },
  ],
  hours: [
    { from: "0", to: "1", label: "0–1h" },
    { from: "1", to: "2", label: "1–2h" },
    { from: "2", to: "4", label: "2–4h" },
    { from: "4", to: "8", label: "4–8h" },
  ],
};

const getInitialFilterMode = (filterValue: unknown): DurationFilterMode =>
  isDurationFilterRangeValue(filterValue) ? "range" : "exact";

const getInitialFilterUnit = (filterValue: unknown): DurationFilterUnit => {
  if (isDurationFilterRangeValue(filterValue)) {
    return filterValue.unit;
  }

  if (
    filterValue &&
    typeof filterValue === "object" &&
    !Array.isArray(filterValue) &&
    "unit" in filterValue &&
    ((filterValue as DurationFilterValue).unit === "minutes" ||
      (filterValue as DurationFilterValue).unit === "hours")
  ) {
    return (filterValue as DurationFilterValue).unit;
  }

  return "minutes";
};

const getInitialFilterAmount = (filterValue: unknown): string => {
  if (
    filterValue &&
    typeof filterValue === "object" &&
    !Array.isArray(filterValue) &&
    "amount" in filterValue
  ) {
    return String((filterValue as DurationFilterValue).amount ?? "");
  }

  return "";
};

const getInitialRangeAmount = (
  filterValue: unknown,
  boundary: "from" | "to",
): string => {
  if (isDurationFilterRangeValue(filterValue)) {
    return String(filterValue[boundary] ?? "");
  }

  return "";
};

type PendingFilterCommit = { value: unknown } | null;

const syncDraftStateFromValue = (
  value: unknown,
  setters: {
    setDraftMode: (mode: DurationFilterMode) => void;
    setDraftUnit: (unit: DurationFilterUnit) => void;
    setDraftAmount: (amount: string) => void;
    setDraftFrom: (from: string) => void;
    setDraftTo: (to: string) => void;
  },
) => {
  setters.setDraftMode(getInitialFilterMode(value));
  setters.setDraftUnit(getInitialFilterUnit(value));
  setters.setDraftAmount(getInitialFilterAmount(value));
  setters.setDraftFrom(getInitialRangeAmount(value, "from"));
  setters.setDraftTo(getInitialRangeAmount(value, "to"));
};

const getChipStyles = (isSelected: boolean) => ({
  borderColor: isSelected ? "transparent" : "rgba(140, 30, 200, 0.18)",
  background: isSelected
    ? "linear-gradient(135deg, var(--newWork) 0%, var(--newWork80) 100%)"
    : "#fff",
  color: isSelected ? "#fff" : "var(--newWork)",
  fontWeight: 600,
  boxShadow: isSelected ? "0 2px 8px rgba(140, 30, 200, 0.24)" : "none",
  "&:hover": {
    background: isSelected
      ? "linear-gradient(135deg, var(--newWork) 0%, var(--newWork80) 100%)"
      : "var(--newWork30)",
  },
});

export default function GenericCrudTableDurationFilter({ column }: any) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const filterValue = column.getFilterValue();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [draftMode, setDraftMode] = useState<DurationFilterMode>(() =>
    getInitialFilterMode(filterValue),
  );
  const [draftUnit, setDraftUnit] = useState<DurationFilterUnit>(() =>
    getInitialFilterUnit(filterValue),
  );
  const [draftAmount, setDraftAmount] = useState(() =>
    getInitialFilterAmount(filterValue),
  );
  const [draftFrom, setDraftFrom] = useState(() =>
    getInitialRangeAmount(filterValue, "from"),
  );
  const [draftTo, setDraftTo] = useState(() =>
    getInitialRangeAmount(filterValue, "to"),
  );

  const filterValueRef = useRef(filterValue);
  filterValueRef.current = filterValue;
  const pendingFilterCommitRef = useRef<PendingFilterCommit>(null);

  const draftSetters = {
    setDraftMode,
    setDraftUnit,
    setDraftAmount,
    setDraftFrom,
    setDraftTo,
  };

  const syncDraftFromFilterValue = useCallback(() => {
    syncDraftStateFromValue(filterValueRef.current, draftSetters);
  }, [setDraftAmount, setDraftFrom, setDraftMode, setDraftTo, setDraftUnit]);

  const handlePopoverExited = useCallback(() => {
    const pendingCommit = pendingFilterCommitRef.current;
    pendingFilterCommitRef.current = null;

    if (pendingCommit) {
      column.setFilterValue(pendingCommit.value);
      syncDraftStateFromValue(pendingCommit.value, draftSetters);
      return;
    }

    syncDraftFromFilterValue();
  }, [column, syncDraftFromFilterValue]);

  const closePopoverWithDeferredCommit = (nextValue: unknown) => {
    pendingFilterCommitRef.current = { value: nextValue };
    setPopoverOpen(false);
  };

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }

    syncDraftFromFilterValue();
  }, [popoverOpen, syncDraftFromFilterValue]);

  const displayValue = formatDurationFilterValue(filterValue);

  const openPopover = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPopoverOpen(true);
  };

  const applyDurationFilter = (
    amount = draftAmount,
    unit = draftUnit,
    closePopover = true,
  ) => {
    const normalizedAmount = normalizeDurationFilterAmount(amount, unit);
    const nextValue =
      normalizedAmount === undefined
        ? undefined
        : {
          amount: normalizedAmount,
          unit,
        };

    if (closePopover) {
      closePopoverWithDeferredCommit(nextValue);
      return;
    }

    column.setFilterValue(nextValue);
  };

  const applyDurationRangeFilter = (
    from = draftFrom,
    to = draftTo,
    unit = draftUnit,
    closePopover = true,
  ) => {
    const normalizedFrom = normalizeDurationFilterAmount(from, unit);
    const normalizedTo = normalizeDurationFilterAmount(to, unit);

    const nextValue =
      normalizedFrom === undefined && normalizedTo === undefined
        ? undefined
        : ({
          mode: "range",
          from: normalizedFrom ?? "",
          to: normalizedTo ?? "",
          unit,
        } as DurationFilterRangeValue);

    if (closePopover) {
      closePopoverWithDeferredCommit(nextValue);
      return;
    }

    column.setFilterValue(nextValue);
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
    setter: (value: string) => void,
  ) => {
    const nextAmount = event.target.value.replace(/[^\d.,]/g, "");
    setter(nextAmount);
  };

  const handleInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    applyFilter: () => void,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyFilter();
      return;
    }

    if (
      draftUnit === "minutes" &&
      (event.key === "." || event.key === ",")
    ) {
      event.preventDefault();
    }
  };

  const handleUnitChange = (
    _event: MouseEvent<HTMLElement>,
    nextUnit: DurationFilterUnit | null,
  ) => {
    if (!nextUnit) {
      return;
    }

    setDraftUnit(nextUnit);
  };

  const handleModeChange = (
    _event: MouseEvent<HTMLElement>,
    nextMode: DurationFilterMode | null,
  ) => {
    if (!nextMode) {
      return;
    }

    setDraftMode(nextMode);
  };

  const handleClear = (event?: MouseEvent) => {
    event?.stopPropagation();
    if (popoverOpen) {
      closePopoverWithDeferredCommit(undefined);
      return;
    }

    pendingFilterCommitRef.current = null;
    column.setFilterValue(undefined);
  };

  const applyDraftFilter = () => {
    if (draftMode === "range") {
      applyDurationRangeFilter();
      return;
    }
    applyDurationFilter();
  };

  const isQuickValueSelected = (quickValue: string) =>
    draftMode === "exact" &&
    normalizeDurationFilterAmount(draftAmount, draftUnit) ===
    normalizeDurationFilterAmount(quickValue, draftUnit);

  const isQuickRangeSelected = (from: string, to: string) =>
    draftMode === "range" &&
    normalizeDurationFilterAmount(draftFrom, draftUnit) ===
    normalizeDurationFilterAmount(from, draftUnit) &&
    normalizeDurationFilterAmount(draftTo, draftUnit) ===
    normalizeDurationFilterAmount(to, draftUnit);

  return (
    <div ref={anchorRef} className={styles.triggerField}>
      <TextField
        variant="standard"
        fullWidth
        value={displayValue}
        onClick={(event) => {
          event.preventDefault();
          setPopoverOpen(true);
        }}
        placeholder={displayValue ? undefined : t("duration_filter_trigger_placeholder")}
        inputProps={{
          readOnly: true,
          "aria-label": t("duration"),
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {displayValue && (
                <GenericButton
                  variant="icon"
                  color="iconBase"
                  aria-label={t("delete_filter")}
                  onClick={handleClear}
                  onMouseDown={(event: any) => event.preventDefault()}
                >
                  <ClearIcon />
                </GenericButton>
              )}
              <GenericButton
                variant="icon"
                color="iconBase"
                aria-label={t("duration_filter_open")}
                onClick={openPopover}
                onMouseDown={(event: any) => event.preventDefault()}
              >
                <AccessTimeIcon />
              </GenericButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={popoverOpen}
        anchorEl={anchorRef.current}
        onClose={() => {
          pendingFilterCommitRef.current = null;
          setPopoverOpen(false);
        }}
        TransitionProps={{ onExited: handlePopoverExited }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              width: "20rem",
              borderRadius: "var(--radius-medium)",
              overflow: "hidden",
              boxShadow:
                "0 4px 6px rgba(32, 30, 30, 0.04), 0 12px 28px rgba(140, 30, 200, 0.14)",
            },
          },
        }}
      >
        <ToggleButtonGroup
          exclusive
          value={draftMode}
          onChange={handleModeChange}
          fullWidth
          size="small"
          sx={{
            p: "0.5rem 0.875rem 0",
            boxSizing: "border-box",
            background: "linear-gradient(180deg, #ffffff 0%, #faf9fb 100%)",
            "& .MuiToggleButtonGroup-grouped": {
              flex: 1,
              borderColor: "rgba(140, 30, 200, 0.18)",
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.02em",
              textTransform: "none",
              py: "0.375rem",
              color: "var(--warmGrey80)",
            },
            "& .MuiToggleButtonGroup-grouped.Mui-selected": {
              background:
                "linear-gradient(135deg, var(--newWork) 0%, var(--newWork80) 100%)",
              color: "#fff",
            },
          }}
        >
          <ToggleButton value="exact" disableRipple>
            {t("duration_filter_exact")}
          </ToggleButton>
          <ToggleButton value="range" disableRipple>
            {t("duration_filter_range")}
          </ToggleButton>
        </ToggleButtonGroup>

        <div className={styles.popoverContent}>
          <ToggleButtonGroup
            exclusive
            value={draftUnit}
            onChange={handleUnitChange}
            fullWidth
            size="small"
            sx={{
              mb: "0.75rem",
              "& .MuiToggleButtonGroup-grouped": {
                flex: 1,
                borderColor: "rgba(250, 101, 34, 0.22)",
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
                color: "var(--warmGrey80)",
              },
              "& .MuiToggleButtonGroup-grouped.Mui-selected": {
                background:
                  "linear-gradient(135deg, var(--humanFirst) 0%, var(--humanFirst80) 100%)",
                color: "#fff",
              },
            }}
          >
            <ToggleButton value="minutes" disableRipple>
              {t("duration_filter_minutes")}
            </ToggleButton>
            <ToggleButton value="hours" disableRipple>
              {t("duration_filter_hours")}
            </ToggleButton>
          </ToggleButtonGroup>

          {draftMode === "range" ? (
            <div className={styles.rangeInputs}>
              <TextField
                autoFocus
                fullWidth
                value={draftFrom}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(event, setDraftFrom)
                }
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                  handleInputKeyDown(event, () => applyDurationRangeFilter())
                }
                label={t("duration_filter_from")}
                inputProps={{
                  inputMode: draftUnit === "hours" ? "decimal" : "numeric",
                  pattern: draftUnit === "hours" ? "[0-9.,]*" : "[0-9]*",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {draftUnit === "hours" ? "h" : "min"}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-light)",
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #fbfafc 100%)",
                  },
                }}
              />
              <TextField
                fullWidth
                value={draftTo}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(event, setDraftTo)
                }
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                  handleInputKeyDown(event, () => applyDurationRangeFilter())
                }
                label={t("duration_filter_to")}
                inputProps={{
                  inputMode: draftUnit === "hours" ? "decimal" : "numeric",
                  pattern: draftUnit === "hours" ? "[0-9.,]*" : "[0-9]*",
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {draftUnit === "hours" ? "h" : "min"}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "var(--radius-light)",
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #fbfafc 100%)",
                  },
                }}
              />
            </div>
          ) : (
            <TextField
              autoFocus
              fullWidth
              value={draftAmount}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(event, setDraftAmount)
              }
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) =>
                handleInputKeyDown(event, () => applyDurationFilter())
              }
              label={t("duration_filter_amount")}
              inputProps={{
                inputMode: draftUnit === "hours" ? "decimal" : "numeric",
                pattern: draftUnit === "hours" ? "[0-9.,]*" : "[0-9]*",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {draftUnit === "hours" ? "h" : "min"}
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "var(--radius-light)",
                  background:
                    "linear-gradient(180deg, #ffffff 0%, #fbfafc 100%)",
                },
              }}
            />
          )}

          <div className={styles.quickValues}>
            {(draftMode === "range" ? QUICK_RANGES[draftUnit] : QUICK_VALUES[draftUnit]).map(
              (quickValue: any) => {
                const isRangeQuick = typeof quickValue === "object";
                const selected = isRangeQuick
                  ? isQuickRangeSelected(quickValue.from, quickValue.to)
                  : isQuickValueSelected(quickValue);
                return (
                  <Chip
                    key={
                      isRangeQuick
                        ? `${draftUnit}-${quickValue.from}-${quickValue.to}`
                        : `${draftUnit}-${quickValue}`
                    }
                    label={
                      isRangeQuick
                        ? quickValue.label
                        : `${quickValue}${draftUnit === "hours" ? "h" : "m"}`
                    }
                    size="small"
                    onClick={() => {
                      if (isRangeQuick) {
                        setDraftFrom(quickValue.from);
                        setDraftTo(quickValue.to);
                        applyDurationRangeFilter(
                          quickValue.from,
                          quickValue.to,
                          draftUnit,
                        );
                        return;
                      }

                      setDraftAmount(quickValue);
                      applyDurationFilter(quickValue, draftUnit);
                    }}
                    sx={getChipStyles(selected)}
                    variant="outlined"
                  />
                );
              },
            )}
          </div>

          <div className={styles.actions}>
            <GenericButton
              variant="outlined"
              color="tertiary"
              onClick={handleClear}
              disabled={!displayValue}
            >
              {t("clear")}
            </GenericButton>
            <GenericButton
              color="tertiary"
              onClick={applyDraftFilter}
              disabled={
                draftMode === "range"
                  ? !draftFrom.trim() && !draftTo.trim()
                  : !draftAmount.trim()
              }
            >
              {t("apply")}
            </GenericButton>
          </div>
        </div>
      </Popover>
    </div>
  );
}
